/*
 * Models for the Quick create feature.  These include an extended Topic model that includes a quick create child model.
 */

define([
  "backbone",
  "underscore",
  "jquery",
  "messaging/messenger",
  "configuration/properties",
  "modules/shared/sortableList/sortableListModel",
  "modules/topicsSplit/topic/topicModel",
  "modules/quickCreate/template/publishButton.html",
  "modules/quickCreate/template/contentButton.html",
  "modules/quickCreate/template/examplesButton.html",
  "modules/quickCreate/template/responseButton.html",
  "modules/header/kbMenu/kbMenuModel",
], function (
  Backbone,
  _,
  $,
  Messenger,
  Properties,
  SortableListModel,
  TopicModel,
  PublishButton,
  ContentButton,
  ExamplesButton,
  ResponseButton,
  KbMenuModel,
) {
  // Temporary workaround for webpacker
  PublishButton = PublishButton.default || PublishButton;
  ContentButton = ContentButton.default || ContentButton;
  ExamplesButton = ExamplesButton.default || ExamplesButton;
  ResponseButton = ResponseButton.default || ResponseButton;

  var QuickCreateModel = {};

  QuickCreateModel.quickCreateModel = Backbone.Model.extend({
    idAttribute: "intentId",
    defaults: {
      exampleComponent: 0,
      responseComponent: 0,
      contentComponent: 0,
    },
    isNew: function () {
      return false;
    },
    url: function () {
      if (!this.id && this.topic) {
        this.set("intentId", this.topic.get("intentId"));
      }
      var kbId = Messenger.reqres.request("active:kb");
      return Properties.rootURL + "/" + kbId + "/quickcreate/" + this.id;
    },
  });

  QuickCreateModel.quickCreateCollection = Backbone.Collection.extend({
    model: QuickCreateModel.quickCreateModel,
    url: function () {
      var kbId = Messenger.reqres.request("active:kb");
      return Properties.rootURL + "/" + kbId + "/quickcreate";
    },
  });

  QuickCreateModel.quickCreateTopic = TopicModel.topic.extend({
    url: function () {
      var kbId = Messenger.reqres.request("active:kb");
      //use a different url if the topic is new or not
      if (this.isNew()) {
        return Properties.rootURL + "/" + kbId + "/intents";
      }
      //otherwise, use the intent Id
      return Properties.rootURL + "/" + kbId + "/intents/" + this.id;
    },
    quickCreate: function () {
      return this.quickCreateModel;
    },
    setQuickCreate: function (quickCreate) {
      this.quickCreateModel = quickCreate;
      //add variable to subModels array so that it will be saved when the topic is saved
      if (!this.subModels) {
        this.subModels = [];
      }
      this.subModels.push(this.quickCreateModel);
      this.quickCreateModel.topic = this; //pass reference to the parent topic
      //set component data on topic
      this.exampleComponent = quickCreate.get("exampleComponent");
      this.responseComponent = quickCreate.get("responseComponent");
      this.contentComponent = quickCreate.get("contentComponent");
      this.canPublish =
        this.exampleComponent + this.responseComponent + this.contentComponent === 6;
      this.NON_VERSIONED_CREATED = KbMenuModel.statuses.NON_VERSIONED_CREATED;
      this.VERSIONED_CREATED = KbMenuModel.statuses.VERSIONED_CREATED;

      //if any component changes, update the topic's representation of it
      this.listenTo(
        this.quickCreateModel,
        "change:exampleComponent",
        function () {
          this.exampleComponent = this.quickCreateModel.get("exampleComponent");
          this.canPublish =
            this.exampleComponent + this.responseComponent + this.contentComponent === 6;
        },
        this,
      );
      this.listenTo(
        this.quickCreateModel,
        "change:responseComponent",
        function () {
          this.responseComponent = this.quickCreateModel.get("responseComponent");
          this.canPublish =
            this.exampleComponent + this.responseComponent + this.contentComponent === 6;
        },
        this,
      );
      this.listenTo(
        this.quickCreateModel,
        "change:contentComponent",
        function () {
          this.contentComponent = this.quickCreateModel.get("contentComponent");
          this.canPublish =
            this.exampleComponent + this.responseComponent + this.contentComponent === 6;
        },
        this,
      );
    },
  });

  QuickCreateModel.quickCreateTopics = Backbone.Collection.extend({
    model: QuickCreateModel.quickCreateTopic,
    addTopic: function () {
      //create a new default topic and return it
      var newTopic = new QuickCreateModel.quickCreateTopic();
      newTopic.setQuickCreate(new QuickCreateModel.quickCreateModel());
      //Make the model dirty so that it will be saved.
      newTopic.quickCreate().dirty = true;
      this.add(newTopic);
      return newTopic;
    },
  });

  /*
   * This reqres handles getting the full list of "quick-create" topics from the currently active KB.
   * Under the current definition, quick create topics are all topics that have the status "created".
   * This function loads all topics with the status "created" and then loads all of the quick create information.
   * This information refers to the status of each component of a topic (example, response, assoc. content).
   * It is possible that a topic with the status "created" does not have any quick-create info.  This would be a
   * topic which was created outside of the quick-create UI.  If that is the case, then we assign a quick create
   * model with a status of 1 ("started") to all components.  Note that this may not be 100% correct.  The goal in
   * doing this is allow for quick create authors and advanced authors to work together on the same topics while
   * keeping the concept of component status limited to the quick create tool.
   */
  Messenger.reqres.setHandler("quick:create:topics", function () {
    //create the quick create topic collection.  We will feed the combined results of the topics collection and quickCreateTopicInfo collection into
    //this collection.  We want the outside application to believe that we do this in one request, so we fake a request on the collection and
    //then manually trigger a 'sync' once the merged data in added to the collection.
    const quickCreateTopics = new QuickCreateModel.quickCreateTopics();
    //fake the request
    quickCreateTopics.trigger("request");
    //use the topic search reqres to get a fake topic group with the search results
    //get the created status Id by finding the status that is marked as the initialStatus
    const topicStatuses = Messenger.reqres.request("kb:statuses");
    //wait until the topic statuses are synced
    topicStatuses.onSynced(function () {
      _handleQuickCreateCollection(quickCreateTopics, topicStatuses.get("allTopicStatuses"));
    }, this);
    return quickCreateTopics;
  });

  //A helper function for the "quick:create:topics" reqres so that the code doesn't get out of control
  function _handleQuickCreateCollection(quickCreateTopics, topicStatuses) {
    const statusId = _.findWhere(topicStatuses, { initialStatus: true }).statusId;
    const topicGroup = Messenger.reqres.request("search:topics:by:status", statusId, ""); //include empty string for status name (not used)
    const topics = topicGroup.topics();
    //now get the quick create info
    const quickCreateTopicInfo = new QuickCreateModel.quickCreateCollection();
    quickCreateTopicInfo.fetch();

    //ok, both the collection of topics and quickCreateTopicInfo are being fetched.  Once they have both synced, combine the results
    topics.onSynced(function () {
      quickCreateTopicInfo.onSynced(function () {
        //go through each topic and apply the quick create topic info, or create one if it doesn't exist
        topics.each(function (topic) {
          const intentId = topic.get("intentId");
          let thisTopicsInfo = quickCreateTopicInfo.findWhere({ intentId: intentId });
          if (!thisTopicsInfo) {
            //create a quick create model with component statuses of 1 ("started")
            thisTopicsInfo = new QuickCreateModel.quickCreateModel({
              intentId: intentId,
              exampleComponent: 1,
              responseComponent: 1,
              contentComponent: 1,
            });
          }
          //add the topic to the quick create topic collection.
          const quickCreateTopic = quickCreateTopics.add(topic.attributes, { silent: true });
          quickCreateTopic.setQuickCreate(thisTopicsInfo);
        });
        //merge complete, trigger a fetched and sync on the collection
        quickCreateTopics.trigger("fetched");
        quickCreateTopics.trigger("sync");
      });
    });
  }

  //Request for the headers of the topics list. Requires the current KB model (may not be synced)
  Messenger.reqres.setHandler("quick:create:headers", function (kbModel) {
    //decide if we use "Publish" or "enable" based on whether response versioning is used

    var Header = SortableListModel.headerModel;
    var publishHeader = new Header({
      headerName: "",
      modelAttribute: "_thisObject.canPublish",
      sortable: true,
      defaultReversed: true,
      numericField: true,
      customHtml: PublishButton,
      width: "15%",
    });
    kbModel.onSynced(function () {
      if (kbModel.get("responseVersioning")) {
        publishHeader.set("headerName", $.t("topic.statuses.publish"));
      } else {
        publishHeader.set("headerName", $.t("topic.statuses.enable"));
      }
    });

    return new Backbone.Collection([
      new Header({ checkbox: true }),
      new Header({
        headerName: $.t("topic.titlePlaceholder"),
        modelAttribute: "intentName",
        editable: true,
        sortable: true,
        filterable: true,
        longTextField: true,
        width: "35%",
      }),
      new Header({
        headerName: $.t("topic.examples.title"),
        modelAttribute: "_thisObject.exampleComponent",
        customHtml: ExamplesButton,
        sortable: true,
        numericField: true,
        width: "15%",
      }),
      new Header({
        headerName: $.t("topic.responseTabs.response"),
        modelAttribute: "_thisObject.responseComponent",
        customHtml: ResponseButton,
        sortable: true,
        numericField: true,
        width: "15%",
      }),
      new Header({
        headerName: $.t("topic.responseProperties.associatedContent"),
        modelAttribute: "_thisObject.contentComponent",
        customHtml: ContentButton,
        sortable: true,
        numericField: true,
        width: "20%",
      }),
      publishHeader,
    ]);
  });

  return QuickCreateModel;
});
